import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

import { STICKY_COLUMN_POSITION } from 'ravenjs/constants/list';
import TableCell from 'ravenjs/lib/TableCell';
import TableHead from 'ravenjs/lib/TableHead';
import TableRow from 'ravenjs/lib/TableRow';
import DataViewFilter from 'ravenjs/components/DataViewFilter';
import { callFunc, getHandler } from 'ravenjs/utils/actions';
import { addStickyOffsetInColumns } from 'ravenjs/utils/table';

function _identity(value) {
    return function() {
        return value;
    };
}

function DataListHead(props) {
    const { actions, schema, theme } = props;
    // Extract info out of the schema.
    const {
        dataViewFilters,
        headerCellStyle = {},
        refs = {},
        rows: rowProps = {},
        selected: selectedProps = {},
        sorting = {},
    } = schema;
    let columns = _get(schema, 'columns', []);
    const { renderSelectableRef, width } = selectedProps;
    const cellTypes = _get(schema, 'cellTypes', {});
    const renderSelectable = getHandler(actions, renderSelectableRef);
    const sortSchemaActive = !_isEmpty(sorting);
    const handleSortChange = getHandler(
        actions,
        sortSchemaActive && refs.onChangeSortRef,
        _identity
    );
    const hasSelectColumn = typeof renderSelectable === 'function';
    const selectColumnWidth = width || '50px';

    const hasStickyColumnsAtLeft = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.LEFT)
    );
    const hasStickyColumnsAtRight = Boolean(
        columns.find(({ sticky }) => sticky && sticky.position === STICKY_COLUMN_POSITION.RIGHT)
    );

    if (hasStickyColumnsAtLeft) {
        columns = addStickyOffsetInColumns(
            columns,
            STICKY_COLUMN_POSITION.LEFT,
            hasSelectColumn ? selectColumnWidth : '0px',
            theme
        );
    }

    if (hasStickyColumnsAtRight) {
        columns = addStickyOffsetInColumns(columns, STICKY_COLUMN_POSITION.RIGHT, '0px', theme);
    }

    function isHeadSortable(headCol) {
        return typeof headCol.sortable === 'boolean' ? headCol.sortable : true;
    }

    return (
        <TableHead>
            <TableRow {...rowProps} zebra={false}>
                {typeof renderSelectable === 'function' && (
                    <TableCell
                        as="th"
                        key="table_cell__selectable"
                        type="head"
                        width={width}
                        style={headerCellStyle}
                    >
                        {callFunc(renderSelectable, 'head')}
                    </TableCell>
                )}
                {columns.map((headCol, headColIdx) => {
                    const headCellType = _get(headCol, 'cellProps.headCellType', null);
                    const {
                        actions: headColActions,
                        cellProps: headColCellProps,
                        filter: filterProps,
                        ...restHeadCol
                    } = headCol;
                    const { align, title } = restHeadCol;
                    const sortableHead = sortSchemaActive && isHeadSortable(headCol);
                    const cellProps = {
                        ...headColCellProps,
                        cellHead:
                            _get(headCol, 'cellProps.cellType', '') === 'editable'
                                ? 'custom'
                                : null,
                        cellType: sortableHead
                            ? 'sortable'
                            : _get(headCol, 'cellProps.cellType', ''),
                        headCellType,
                    };
                    const infoText = _get(headCol, 'infoText', null);
                    const onClickCell =
                        sortableHead && headCol.id
                            ? () => handleSortChange(sorting.order, headCol.sortId || headCol.id)
                            : null;
                    const key = headCol.keyProp || `${headCol.id}_${headCol.title}`;
                    const idFilter = `${headCol.id}__filter`;
                    const filter =
                        filterProps && filterProps.options ? (
                            <DataViewFilter
                                handlers={actions}
                                options={filterProps.options}
                                id={idFilter}
                                type="column"
                                defaultValue={dataViewFilters ? dataViewFilters[idFilter] : []}
                            />
                        ) : (
                            ''
                        );

                    return (
                        <TableCell
                            as="th"
                            actions={headColActions || []}
                            align={align}
                            cellProps={cellProps}
                            col={restHeadCol}
                            config={headCol}
                            handlers={actions}
                            key={key}
                            onSort={onClickCell}
                            sortSchema={sorting}
                            infoText={infoText}
                            style={headerCellStyle}
                            type="head"
                            cellTypes={cellTypes}
                        >
                            <span onClick={onClickCell}>{title}</span>
                            {filter}
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}

DataListHead.propTypes = {
    actions: PropTypes.object,
    schema: PropTypes.object,
    theme: PropTypes.object.isRequired,
};

DataListHead.defaultProps = {
    actions: {},
    schema: {},
};

export { DataListHead as DataListHeadUnwrapped };
export default withTheme(DataListHead);
